import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { GOALS } from "@/lib/brand";

export default function GoalsSection() {
  return (
    <section
      id="goals"
      data-testid="goals-section"
      className="relative py-24 md:py-32 bg-[#F3F5F4]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 md:mb-16 items-end">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[#00A65A]">
              <span className="w-6 h-px bg-[#00A65A]" />
              The 6 Pillars
            </span>
            <h2
              data-testid="goals-headline"
              className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#0B1B3D] leading-[1.04]"
            >
              Your goals, guided by{" "}
              <span className="italic font-light text-[#00A65A]">expert planning.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-[#0B1B3D]/70 leading-relaxed">
              Your investments should support the goals that matter most in life.
              We map every rupee — from your first SIP to your last EMI — to a story
              you actually want to live.
            </p>
          </div>
        </div>

        {/* Bento Tetris grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[260px] md:auto-rows-[300px]">
          {GOALS.map((g, i) => (
            <motion.a
              key={g.id}
              href="#contact"
              data-testid={`goal-card-${g.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.07 }}
              className={`bento-card group relative overflow-hidden rounded-3xl ${g.span} md:col-span-12 ${g.id === 'vacation' || g.id === 'wealth' ? 'md:col-span-6 lg:col-span-3' : g.id === 'home' || g.id === 'child-education' || g.id === 'emergency' ? 'md:col-span-6' : 'md:col-span-12 lg:col-span-6'} ${g.id === 'retirement' ? 'lg:row-span-2' : ''} bg-[#0B1B3D]`}
            >
              {/* Background image */}
              <div className="absolute inset-0">
                <img
                  src={g.image}
                  alt={g.title}
                  className="bento-img w-full h-full object-cover"
                />
              </div>
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D]/95 via-[#0B1B3D]/40 to-transparent" />

              {/* Top tag */}
              <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/15">
                  {g.accent}
                </span>
                <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center group-hover:bg-[#00A65A] group-hover:border-[#00A65A] transition-colors">
                  <ArrowUpRight className="w-4 h-4 text-white" />
                </div>
              </div>

              {/* Bottom content */}
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
                <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium text-white leading-tight">
                  {g.title}
                </h3>
                <p className="mt-2 text-sm md:text-base text-white/75 max-w-md">
                  {g.blurb}
                </p>
              </div>

              {/* Number index */}
              <div className="absolute top-5 right-20 hidden md:block">
                <span className="font-display text-white/30 text-sm">
                  0{i + 1}
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
